import { useEffect, useRef } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { connectInventoryHub, type LowStockPayload } from '../api/common/InventoryHub';
import { tokenStore } from '../api/tokenStore';

/**
 * Subscribes to the `OnLowStock` event pushed by ProductService's InventoryHub (via
 * `/hubs/inventory`). Connects on mount, disconnects on unmount. Uses the minimal direct
 * connectInventoryHub client (same proven pattern as connectMessagesHub), not
 * ChatHubService/SignalRServiceBase — see MessagesHub.ts's doc comment for why that base
 * class is untested boilerplate best avoided for new work.
 */
export function useInventoryAlerts(onLowStock?: (payload: LowStockPayload) => void) {
  const onLowStockRef = useRef(onLowStock);
  onLowStockRef.current = onLowStock;

  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    const token = tokenStore.get()?.accessToken;
    if (!token) return;

    const connection = connectInventoryHub(token, (payload) => onLowStockRef.current?.(payload));
    connectionRef.current = connection;

    return () => {
      connection.stop().catch(() => {
        /* best-effort — the tab is navigating away or unmounting either way */
      });
      connectionRef.current = null;
    };
  }, []);
}
